import {
  CustomButton,
  CustomCollapseCard,
  CustomForm,
  CustomFormItem,
  CustomImage,
  CustomMoneyInput,
  errorDialog,
  successDialog,
  Text,
  useText
} from '@src/components';
import '@src/styles/balanceUpload/monthlyOrder.scss';
import { Alert, Form } from 'antd';
import React, { useEffect, useState } from 'react';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
  getLoadBalanceDetailsGetPagedList,
  isDetailFilterActive,
  loadBalanceDetailPriceUpdate,
  setBalanceNotIncludedCards,
  setBalanceUploadDetails,
  setSelectedBalanceUploadDetails
} from '@src/store/slice/balanceUploadDetailSlice';
import {
  loadBalancesUpdate,
  setCurrentLoadBalance,
  setIsAllSelectedRowKeys,
  setSelectedRowKeys,
} from '@src/store/slice/balanceUploadSlice';
import { isGreaterMaximumLoadedAmount } from '@src/utils/utils';
import warning from '@src/assets/icons/icon-profil-warning-dark.svg';
import iconInfo from '@src/assets/icons/icon-info-blue.svg';
import { applyPriceToDetails, getSelectedBalanceDetails } from './balanceUploadHelpers';

const MonthlyOrder = () => {
  const isMobil = useMediaQuery({ query: '(max-width: 1015.98px)' });

  const dispatch = useDispatch();
  const store = useStore();
  const [form] = Form.useForm();

  const { selectedCustomer } = useSelector((state) => state?.customer);
  const { maximumLoadedAmount } = useSelector((state) => state?.orderLimits);
  const { currentLoadBalance, isAllSelectedRowKeys, selectedRowKeys } = useSelector(
    (state) => state?.balanceUpload,
  );
  const { balanceUploadDetails, balanceFilterItems, balanceNotIncludedCards, detailTableFilter } =
    useSelector((state) => state?.balanceUploadDetail);


  const [monthlyPrice, setMonthlyPrice] = useState(currentLoadBalance.monthlyPrice);

  const amountShouldNotBeAboveText = useText('amountShouldNotBeAbove');
  const transactionSuccessfullyCompleted2 = useText('transactionSuccessfullyCompleted2');
  useEffect(() => {
    if (currentLoadBalance) {
      form.setFieldsValue({
        monthlyPrice: monthlyPrice || currentLoadBalance.monthlyPrice,
      });
    }
  }, [currentLoadBalance, form]);

  const applyToAll = async (onlySelected) => {
    try {
      const values = await form.validateFields();
      if (
        isGreaterMaximumLoadedAmount({
          controlValue: maximumLoadedAmount,
          price: values?.monthlyPrice,
        })
      ) {
        errorDialog({
          title: <Text t="error" />,
          message: amountShouldNotBeAboveText.replaceAll('{amount}', maximumLoadedAmount),
        });
        return;
      }
      await dispatch(loadBalancesUpdate({ ...currentLoadBalance, ...values, periodType: 1 }));

      const justCardNumberFilterItems = balanceFilterItems?.map(item => item.cardNumber) || [];
      const notSelectedCards = balanceNotIncludedCards?.map(item => item.cardNumber) || [];

      let data = {
        dayCount: undefined,
        price: values.monthlyPrice,
        loadBalanceId: currentLoadBalance.id,
        cardNumber: balanceFilterItems?.length === 0 ? null : justCardNumberFilterItems,
        customerId: selectedCustomer?.customerId,
        notIncludedCards: !onlySelected || notSelectedCards.length === 0 ? null : notSelectedCards,
      };

      const res = await dispatch(loadBalanceDetailPriceUpdate(data));

      if (res.payload.isSuccess) {
        successDialog({
          title: <Text t="success" />,
          message: res?.payload.message || transactionSuccessfullyCompleted2,
          onOk: async () => {
            await dispatch(setSelectedRowKeys([]));
            await dispatch(setIsAllSelectedRowKeys(false));
            await dispatch(setBalanceNotIncludedCards([]));
            await dispatch(
              getLoadBalanceDetailsGetPagedList({
                body: {},
                pageNumber: 1,
              }),
            );
          },
        });
      }
    } catch (e) { }
  };

  const submitAll = () => applyToAll(false);

  const submitSelected = async () => {
    // With an active filter, "select all" only covers the filtered rows, so apply to the explicit selection.
    if (isAllSelectedRowKeys && !isDetailFilterActive(detailTableFilter.body)) {
      await applyToAll(true);
      return;
    }
    try {
      const values = await form.validateFields();
      if (
        isGreaterMaximumLoadedAmount({
          controlValue: maximumLoadedAmount,
          price: values?.monthlyPrice,
        })
      ) {
        errorDialog({
          title: <Text t="error" />,
          message: amountShouldNotBeAboveText.replaceAll('{amount}', maximumLoadedAmount),
        });
        return;
      }
      const selectedDetails = getSelectedBalanceDetails(selectedRowKeys, balanceUploadDetails);
      if (selectedDetails.length > 0) {
        await dispatch(loadBalancesUpdate({ ...currentLoadBalance, ...values, periodType: 1 }));

        let data = {
          price: values.monthlyPrice,
          idList: selectedDetails.map((item) => item.id),
          loadBalanceId: currentLoadBalance.id,
          cardNumber: selectedDetails.map((item) => item.cardNumber),
          customerId: selectedCustomer?.customerId,
        };
        const res = await dispatch(loadBalanceDetailPriceUpdate(data));
        if (res.payload.isSuccess) {
          dispatch(
            setBalanceUploadDetails(
              applyPriceToDetails(
                store.getState().balanceUploadDetail.balanceUploadDetails,
                selectedDetails,
                { price: values.monthlyPrice },
              ),
            ),
          );
          successDialog({
            title: <Text t="success" />,
            message: res?.payload.message || transactionSuccessfullyCompleted2,
            onOk: async () => {
              await dispatch(setSelectedRowKeys([]));
              await dispatch(setIsAllSelectedRowKeys(false));
              await dispatch(setBalanceNotIncludedCards([]));
              await dispatch(setSelectedBalanceUploadDetails([]));
            },
          });
        }
      } else {
        errorDialog({
          title: <Text t="error" />,
          htmlContent: <Text t="noSelectedRecord" />,
        });
      }
    } catch (e) { }
  };

  const handleMonthlyPrice = async (val) => {
    setMonthlyPrice(val);
    dispatch(setCurrentLoadBalance({ monthlyPrice: val }));
  };

  return (
    <CustomCollapseCard className="monthly-order-card" cardTitle={<Text t="monthlyOrder" />}>
      <div className="monthly-order-container">
        <Alert
          message={
            <div className="alert-message-contain">
              <CustomImage src={warning} />{' '}
              <div className="description">
                <div dangerouslySetInnerHTML={{ __html: currentLoadBalance.info }} />
              </div>
            </div>
          }
          banner
          showIcon={false}
          className="custom-alert-warning"
          closable
        />
        <CustomForm
          name="monthlyOrderForm"
          className="monthly-order-form"
          initialValues={{}}
          autoComplete="off"
          layout={'horizontal'}
          form={form}
        >
          <div className="form-item">
            <div className="monthly-amount-content">
              <div className='flex'>
                <CustomFormItem
                  label={<Text t="howMuchDoYouWantToLoadPer" />}
                  name="monthlyPrice"
                  className="monthly-amount-form-item"
                  rules={[{ required: true, message: <Text t="requiredField2" /> }]}
                >
                  <CustomMoneyInput
                    id="ticketRestaurant-balanceUpload-balanceInformation-monthlyOrder-monthlyPrice-moneyInput"
                    value={monthlyPrice}
                    autoComplete="off"
                    height={40}
                    width={88}
                    onChange={handleMonthlyPrice}
                  />
                  <span className="info-text"><Text t="tlMonth" /> <br /><Text t="vatIncluded" /></span>
                </CustomFormItem>
              </div>
              <Alert
                message={
                  <div className="alert-message-contain">
                    <div>
                      <CustomImage src={iconInfo} />{' '}
                    </div>
                    <div className="description">
                      <Text t="thisAmountIsBasedOnTheNumberOf" /> <br /> <Text t="calculatedBasedOnTheDailyMealAllowanceExemption" /> <br /><Text t="isCalculatedYouCanChangeItIfYou" />
                    </div>
                  </div>
                }
                banner
                showIcon={false}
                className="custom-alert-info"
              />
            </div>
            <CustomFormItem label={false} className="w-full">
              {!isMobil && (
                <div className="action-buttons">
                  <CustomButton
                    id="ticketRestaurant-balanceUpload-balanceInformation-monthlyOrder-cancel-btn"
                    className="cancel-btn"
                    onClick={submitSelected}
                  >
                    <Text t="applyToSelected2" />
                  </CustomButton>
                  <CustomButton
                    id="ticketRestaurant-balanceUpload-balanceInformation-monthlyOrder-primary-btn"
                    className="primary-btn"
                    onClick={submitAll}
                  >
                    <Text t="applyToAll2" />
                  </CustomButton>
                </div>
              )}
            </CustomFormItem>
          </div>
        </CustomForm>
        <hr />
        {isMobil && (
          <div className="action-buttons">
            <CustomButton
              id="ticketRestaurant-balanceUpload-balanceInformation-monthlyOrder-second-cancel-btn"
              className="cancel-btn"
              onClick={submitSelected}
            >
              <Text t="applyToSelected2" />
            </CustomButton>
            <CustomButton
              id="ticketRestaurant-balanceUpload-balanceInformation-monthlyOrder-second-primary-btn"
              className="primary-btn"
              onClick={submitAll}
            >
              <Text t="applyToAll2" />
            </CustomButton>
          </div>
        )}
      </div>
    </CustomCollapseCard>
  );
};

export default MonthlyOrder;
